type RecaptchaV2Provider = {
  render: (
    containerId: string,
    options?: {
      'theme'?: 'light' | 'dark';
      'size'?: 'normal' | 'compact';
      'callback'?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => Promise<boolean | null>;
  reset: (containerId?: string) => boolean;
  destroy: (containerId?: string) => boolean;
  preload: () => Promise<boolean>;
};

export default defineNuxtPlugin(() => {
  const {
    public: { recaptchaV2Key },
  } = useRuntimeConfig();

  const fallback: RecaptchaV2Provider = {
    render: async () => null,
    reset: () => false,
    destroy: () => false,
    preload: async () => false,
  };

  if (!recaptchaV2Key) {
    console.warn('reCAPTCHA v2: key is missing');
    return {
      provide: {
        recaptchaV2: fallback,
      },
    };
  }

  const siteKey = recaptchaV2Key as string;

  const isRecaptchaLoaded = ref(false);
  const isRecaptchaLoading = ref(false);
  const activeContainers = new Set<string>();

  const loadRecaptchaScript = () => {
    return new Promise<void>((resolve, reject) => {
      if (isRecaptchaLoaded.value) {
        resolve();
        return;
      }

      if (isRecaptchaLoading.value) {
        const checkIfLoaded = setInterval(() => {
          if (isRecaptchaLoaded.value) {
            clearInterval(checkIfLoaded);
            resolve();
          }
        }, 100);
        return;
      }

      isRecaptchaLoading.value = true;

      const globalStore = useGlobalStore();
      const { currentLocale } = globalStore;

      window.onRecaptchaLoadCallback = () => {
        isRecaptchaLoaded.value = true;
        isRecaptchaLoading.value = false;
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoadCallback&render=explicit&hl=${currentLocale?.code}`;
      script.async = true;
      script.defer = true;

      script.onerror = (error) => {
        isRecaptchaLoading.value = false;
        reject(error);
      };

      document.head.appendChild(script);
    });
  };

  const preloadRecaptchaScript = async () => {
    try {
      await loadRecaptchaScript();
      return true;
    }
    catch (error) {
      console.error('Error preload reCAPTCHA v2:', error);
      return false;
    }
  };

  const renderRecaptcha = async (
    containerId: string,
    options: {
      'theme'?: 'light' | 'dark';
      'size'?: 'normal' | 'compact';
      'callback'?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    } = {},
  ) => {
    try {
      await loadRecaptchaScript();

      if (!window.grecaptcha || !window.grecaptcha.render) {
        console.error('reCAPTCHA v2: API not ready');
        return false;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`reCAPTCHA v2: Container with id "${containerId}" not found`);
        return false;
      }

      if (activeContainers.has(containerId)) {
        container.innerHTML = '';
      }

      window.grecaptcha.render(containerId, {
        'sitekey': siteKey,
        'theme': options.theme || 'light',
        'size': options.size || 'normal',
        'callback': options.callback,
        'expired-callback': options['expired-callback'],
        'error-callback': options['error-callback'],
      });

      activeContainers.add(containerId);
      return true;
    }
    catch (error) {
      console.error('Error rendering reCAPTCHA v2:', error);
      return false;
    }
  };

  const resetRecaptcha = (containerId?: string) => {
    try {
      if (!isRecaptchaLoaded.value || !window.grecaptcha || !window.grecaptcha.reset) {
        console.warn('reCAPTCHA v2: API not ready');
        return false;
      }

      if (containerId && !activeContainers.has(containerId)) {
        console.warn(`reCAPTCHA v2: Container "${containerId}" not found in active containers`);
        return false;
      }

      window.grecaptcha.reset();
      return true;
    }
    catch (error) {
      console.error('Error resetting reCAPTCHA v2:', error);
      return false;
    }
  };

  const destroyRecaptcha = (containerId?: string) => {
    try {
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          container.innerHTML = '';
        }
        activeContainers.delete(containerId);
      }
      else {
        for (const id of activeContainers) {
          const container = document.getElementById(id);
          if (container) {
            container.innerHTML = '';
          }
        }
        activeContainers.clear();
      }

      return true;
    }
    catch (error) {
      console.error('Error destroying reCAPTCHA v2:', error);
      return false;
    }
  };

  const recaptcha: RecaptchaV2Provider = {
    render: renderRecaptcha,
    reset: resetRecaptcha,
    destroy: destroyRecaptcha,
    preload: preloadRecaptchaScript,
  };

  return {
    provide: {
      recaptchaV2: recaptcha,
    },
  };
});
